'use client';

import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createSubAccount, fetchMySubAccounts } from '../redux/slices/masterSubAccountSlice';
import type { RootState, AppDispatch } from '../redux/store';
import { useTranslation } from 'react-i18next';
import { logger } from '../utils/logger';

interface CreateSubAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

interface FormData {
  subAccountName: string;
  subAccountCompany: string;
  subAccountPassword: string;
}

interface FormErrors {
  subAccountName?: string;
  subAccountCompany?: string;
  subAccountPassword?: string;
}

const CreateSubAccountModal: React.FC<CreateSubAccountModalProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const { t } = useTranslation(['modal', 'common']);
  const dispatch = useDispatch<AppDispatch>();
  const { loading, errors: reduxErrors } = useSelector((state: RootState) => state.masterSubAccount);
  const reduxError = reduxErrors.createSubAccount;
  
  const [formData, setFormData] = useState<FormData>({
    subAccountName: '',
    subAccountCompany: '',
    subAccountPassword: ''
  });
  
  const [errors, setErrors] = useState<FormErrors>({});
  const [showPassword, setShowPassword] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setFormData({
        subAccountName: '',
        subAccountCompany: '',
        subAccountPassword: ''
      });
      setErrors({});
      setShowPassword(false);
      setSuccessMessage('');
    }
  }, [isOpen]);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    
    // Name validation
    if (!formData.subAccountName.trim()) {
      newErrors.subAccountName = t('modal:nameRequired');
    }
    
    // Company validation
    if (!formData.subAccountCompany.trim()) {
      newErrors.subAccountCompany = t('modal:companyRequired');
    }
    
    // Password validation (matches backend requirements)
    if (!formData.subAccountPassword) {
      newErrors.subAccountPassword = t('modal:passwordRequired');
    } else if (formData.subAccountPassword.length < 8) {
      newErrors.subAccountPassword = t('modal:passwordMinLength');
    } else {
      const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/;
      if (!passwordRegex.test(formData.subAccountPassword)) {
        newErrors.subAccountPassword = t('modal:passwordRequirements');
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    try {
      await dispatch(createSubAccount({
        subAccountName: formData.subAccountName.trim(),
        company: formData.subAccountCompany.trim(),
        subAccountPassword: formData.subAccountPassword
      })).unwrap();

      // Success! Show success message
      setSuccessMessage(t('modal:createdSuccessfully'));
      
      // Refresh sub-accounts list
      await dispatch(fetchMySubAccounts()).unwrap();
      
      // Call onSuccess callback if provided
      if (onSuccess) {
        onSuccess();
      }
      
      // Close modal after a short delay
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      logger.error('Failed to create sub-account:', err);
      // Error will be displayed from Redux state
    }
  };

  const handleCancel = () => {
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={handleCancel}
      />
      
      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
          {/* Header */}
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900">{t('modal:createSubAccount')}</h2>
            <p className="mt-1 text-sm text-gray-900">
              {t('modal:createSubAccount')}
            </p>
          </div>

          {/* Success Message */}
          {successMessage && (
            <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded-lg">
              {successMessage}
            </div>
          )}

          {/* Error Message */}
          {reduxError && !successMessage && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg text-sm">
              {reduxError}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">
            {/* Account Name Input */}
            <div>
              <label htmlFor="subAccountName" className="block text-sm font-medium text-gray-900 mb-1">
                {t('modal:subAccountName')} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="subAccountName"
                name="subAccountName"
                autoComplete="off"
                value={formData.subAccountName}
                onChange={(e) => setFormData({ ...formData, subAccountName: e.target.value })}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.subAccountName ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder={t('modal:subAccountName')}
                disabled={loading.createSubAccount || !!successMessage}
              />
              {errors.subAccountName && (
                <p className="mt-1 text-sm text-red-600">{errors.subAccountName}</p>
              )}
              <p className="mt-1 text-xs text-gray-600">
                {t('modal:subAccountNameDescription')}
              </p>
            </div>

            {/* Company Input */}
            <div>
              <label htmlFor="subAccountCompany" className="block text-sm font-medium text-gray-900 mb-1">
                {t('modal:company')} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="subAccountCompany"
                name="subAccountCompany"
                autoComplete="off"
                value={formData.subAccountCompany}
                onChange={(e) => setFormData({ ...formData, subAccountCompany: e.target.value })}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.subAccountCompany ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder={t('modal:company')}
                disabled={loading.createSubAccount || !!successMessage}
              />
              {errors.subAccountCompany && (
                <p className="mt-1 text-sm text-red-600">{errors.subAccountCompany}</p>
              )}
            </div>

            {/* Password Input */}
            <div>
              <label htmlFor="subAccountPassword" className="block text-sm font-medium text-gray-900 mb-1">
                {t('modal:password')} <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="subAccountPassword"
                  name="new-password"
                  autoComplete="new-password"
                  value={formData.subAccountPassword}
                  onChange={(e) => setFormData({ ...formData, subAccountPassword: e.target.value })}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 pr-10 ${
                    errors.subAccountPassword ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder={t('modal:password')}
                  disabled={loading.createSubAccount || !!successMessage}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-800 hover:text-gray-900"
                  disabled={loading.createSubAccount || !!successMessage}
                >
                  {showPassword ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  )}
                </button>
              </div>
              {errors.subAccountPassword && (
                <p className="mt-1 text-sm text-red-600">{errors.subAccountPassword}</p>
              )}
              <p className="mt-1 text-xs text-gray-800">
                {t('modal:passwordRequirements')}
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={handleCancel}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-900 rounded-lg hover:bg-gray-50 transition-colors"
                disabled={loading.createSubAccount}
              >
                {t('common:cancel')}
              </button>
              <button
                type="submit"
                disabled={loading.createSubAccount || !!successMessage}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {loading.createSubAccount ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {t('modal:creating')}
                  </>
                ) : (
                  t('modal:create')
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateSubAccountModal;


